// THIS COMPONENT SHOWS ALL THE MAIN CONTENT OF EACH SECTION OF THE SIDEBOARD
import { Bell } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import NotificationCenter from './NotificationCenter';
import Tasks from './Tasks';
import FolderScreen from './FolderScreen';
import useFolder from './useFolder'; // Custom hook for folder management
import { error as logError } from "../utils/logger";
const MainContent = ({
  category,
  notifications,
  unreadCount,
  setNotifications,
  setUnreadCount,
  addTaskSignal
}) => {
  const [showNotifCenter, setShowNotifCenter] = useState(false);

  // Folder state lives here
  const { folders, fetchFolders, createFolder} = useFolder();
  const [selectedFolder, setSelectedFolder] = useState(null);

  const isShared =
    category === 'Tasks Delegated - Assigned' ||
    category === 'Tasks Delegated - Invitation';

  const getButtonLabel = () => {
    if (category === 'Tasks Delegated - Assigned') return 'Assign Task';
    if (category === 'Tasks Delegated - Invitation') return 'Invite to Task';
    return 'Add Tasks';
  };

  // Load folders on mount
  useEffect(() => {
   fetchFolders(false).catch(logError);
  }, [fetchFolders]);

  // Create folder (modal passes name + color + baseFile + baseFile_label)
   const handleCreateFolder = async (folderName, color, baseFile, baseFile_label) => {
   try{
      await createFolder(folderName, color, baseFile, baseFile_label);
    } catch (e) {
      logError(e);
      alert(e.message || "Failed to create folder");
    }
  };

const handleFolderSelect = (folder) => {
  // Avoid logging sensitive folder data
  
  setSelectedFolder(folder);
};
  const handleBackToAllTasks = () => setSelectedFolder(null);

  const handleFolderDelete = useCallback(async (_id) =>{
    await fetchFolders(false); // Refresh folders after deletion
    setSelectedFolder(null); // Reset selected folder

  }, [fetchFolders]);


  // 🔁 Screen swap: if a folder is selected, render the FolderScreen instead
  if (selectedFolder && category === 'Personal Tasks') {
    
    return (
      <FolderScreen
        selectedFolder={selectedFolder}
        category={category}
        getButtonLabel={getButtonLabel}
        isShared={isShared}
        notifications={notifications}
        unreadCount={unreadCount}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
        onBackToTasks={handleBackToAllTasks}
        onFolderDeleted = {handleFolderDelete}
      />
    );
  }

  // Default (no folder selected): show category header + Tasks view
  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Category and Bell Icon */}
        <header className="flex-shrink-0 flex justify-between items-center p-8 pb-4">
          <h2 className="text-4xl font-semibold text-[#2D3748]">{category}</h2>

          <div className="relative">
            <button
              onClick={() => setShowNotifCenter(!showNotifCenter)}
              className="p-2 rounded-full relative"
              aria-label="Notifications"
            >
              <Bell className="h-6 w-6 text-[#2D3748] hover:text-[#4299E1]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifCenter && (
              <div className="absolute right-0 mt-2 w-[22rem] z-50">
                <NotificationCenter
                  notifications={notifications}
                  setNotifications={setNotifications}
                  setUnreadCount={setUnreadCount}
                  closeCenter={() => setShowNotifCenter(false)}
                />
              </div>
            )}
          </div>
        </header>

        {/* Scrollable Tasks Section */}
        <div className="flex-1 overflow-y-auto px-8 pb-8 scrollbar-hide">
          <Tasks
            category={category}
            getButtonLabel={getButtonLabel}
            isShared={isShared}
            selectedFolder={null}
            folders={folders}
            onCreateFolder={handleCreateFolder}   // (name, color)
            onFolderSelect={handleFolderSelect}   // clicking a pill triggers screen swap
            triggerAddTask={addTaskSignal}
          />
        </div>
      </main>
    </div>
  );
};

export default MainContent;
